export type NodeType = 'normal' | 'source' | 'sink';

export interface Vec2 {
  readonly x: number;
  readonly y: number;
}

export interface GraphNode {
  readonly id: string;
  readonly label: string;
  readonly position: Vec2;
  readonly type: NodeType;
}

export interface GraphEdge {
  readonly id: string;
  readonly sourceId: string;
  readonly targetId: string;
  readonly weight: number;
  readonly capacity: number;
  readonly metadata?: Readonly<Record<string, string>>;
}

export interface Graph {
  readonly nodes: readonly GraphNode[];
  readonly edges: readonly GraphEdge[];
  readonly directed: boolean;
}

const NODE_TYPES: readonly NodeType[] = ['normal', 'source', 'sink'];

function parseNodeType(raw: unknown): NodeType {
  return NODE_TYPES.includes(raw as NodeType) ? (raw as NodeType) : 'normal';
}

export function nodeFromJson(json: Record<string, unknown>): GraphNode {
  return {
    id: json.id as string,
    label: (json.label as string) ?? '',
    position: { x: Number(json.x), y: Number(json.y) },
    type: parseNodeType(json.type),
  };
}

export function edgeFromJson(json: Record<string, unknown>): GraphEdge {
  const metadata = json.metadata as Record<string, string> | undefined;
  return {
    id: json.id as string,
    sourceId: json.source as string,
    targetId: json.target as string,
    weight: json.weight === undefined ? 1 : Number(json.weight),
    capacity: json.capacity === undefined ? 0 : Number(json.capacity),
    ...(metadata ? { metadata } : {}),
  };
}

export function graphFromJson(json: Record<string, unknown>): Graph {
  return {
    directed: (json.directed as boolean) ?? false,
    nodes: (json.nodes as Record<string, unknown>[]).map(nodeFromJson),
    edges: (json.edges as Record<string, unknown>[]).map(edgeFromJson),
  };
}

export function nodeById(graph: Graph, id: string): GraphNode | undefined {
  return graph.nodes.find((n) => n.id === id);
}

export function edgeById(graph: Graph, id: string): GraphEdge | undefined {
  return graph.edges.find((e) => e.id === id);
}

export function edgesFrom(graph: Graph, nodeId: string): GraphEdge[] {
  return graph.edges.filter((e) => e.sourceId === nodeId);
}

export function edgesTo(graph: Graph, nodeId: string): GraphEdge[] {
  return graph.edges.filter((e) => e.targetId === nodeId);
}

export function edgesOf(graph: Graph, nodeId: string): GraphEdge[] {
  return graph.edges.filter((e) => e.sourceId === nodeId || e.targetId === nodeId);
}

export function singleNodeOfType(graph: Graph, type: NodeType): GraphNode | undefined {
  return graph.nodes.find((n) => n.type === type);
}

export function withoutEdges(graph: Graph, edgeIds: ReadonlySet<string>): Graph {
  return {
    nodes: graph.nodes,
    edges: graph.edges.filter((e) => !edgeIds.has(e.id)),
    directed: graph.directed,
  };
}

export function withEdges(graph: Graph, edges: readonly GraphEdge[]): Graph {
  return { nodes: graph.nodes, edges, directed: graph.directed };
}
